import { useState } from 'react'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { qk, invalidate } from '../shared/queries.ts'
import { call } from '../shared/api.ts'

/**
 * ══ CHUÔNG THÔNG BÁO — chép `NotifBell` (`proship-trans/src/components/Layout.tsx:513`) ════════
 *
 * ⭐ Một phiếu được GÁN cho ai đó duyệt thì người đó phải BIẾT, không phải tự đi tìm.
 *
 * ⚠ KHÔNG CÓ NÚT GỬI THƯ. rs-erp chưa có hạ tầng gửi thư (cùng lý do đã ghi ở cửa `/ar/nhac-no`:
 *   soạn sẵn, KHÔNG gửi), nên màn NÓI THẲNG điều đó thay vì dựng một nút không làm gì. Bản cũ
 *   cũng vậy và ghi rõ vì sao: *"trước đây user tưởng app có gửi mail mà mãi không thấy"*.
 * ⚠ Đếm CHƯA ĐỌC lấy từ máy chủ, không đếm trong 20 dòng vừa tải: bỏ quên 50 thông báo cũ thì
 *   chuông vẫn phải nói đúng con số.
 */

interface NotifRow {
  id: string
  kind: 'assign' | 'mention' | 'broadcast'
  byName: string
  title: string
  message: string
  link: string
  readAt: string | null
  createdAt: string
}

const BASE = '/api/v1/thong-bao'

const fetchMine = (): Promise<{ rows: NotifRow[]; unread: number }> =>
  call<{ rows: NotifRow[]; unread: number }>(BASE)

/**
 * ⭐ CHUÔNG TỰ CẬP NHẬT (L20 + X-KH-03, soát 16/10). Bản cũ không có máy chủ: chuông đọc kho
 *   `localStorage` và đồng bộ giữa các tab qua sự kiện `storage` (`proship-trans/src/lib/store.ts:166`)
 *   — thông báo hiện ra NGAY ở mọi tab cùng trình duyệt. rs-erp có máy chủ nên phải tự hỏi lại, không
 *   thì người duyệt ngồi ở `/approvals` không bao giờ thấy phiếu mới gán cho mình cho tới khi F5,
 *   và mở chuông ra còn được bảo "Chưa có thông báo nào".
 *   · Hỏi lại mỗi 60 giây khi tab đang mở (dừng khi tab ẩn — `refetchIntervalInBackground` tắt);
 *   · hỏi lại khi quay về cửa sổ (riêng chuông — cả app tắt cờ này vì lý do bảng nhấp nháy, xem
 *     `query-client.tsx`; chuông chỉ là một con số nhỏ, không có lý do đó);
 *   · hỏi lại khi MỞ chuông.
 */
const POLL_MS = 60_000

/** Nhãn trên chuông: `9+` khi quá 9 — chép bản cũ (`Layout.tsx:539`). */
const badge = (n: number): string => (n > 9 ? '9+' : String(n))

export const NotifBell = ({ meName, onNavigate }: {
  meName: string
  onNavigate: (to: string) => void
}): JSX.Element => {
  const { t } = useTranslation()
  const client = useQueryClient()
  const [open, setOpen] = useState(false)
  const { data } = useQuery({
    queryKey: qk.notifs(),
    queryFn: fetchMine,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  })

  const rows = data?.rows ?? []
  const unread = data?.unread ?? 0

  const refresh = (): void => { invalidate(client, qk.notifs()) }

  const go = (n: NotifRow): void => {
    setOpen(false)
    if (n.readAt === null) {
      // `keepalive` — lệnh "đã đọc" phải tới máy chủ kể cả khi trang bị thay ngay sau đó (X-KH-02:
      // 1/4 lượt mất khi bấm lúc trang còn tải). Điều hướng nay tại chỗ nên hiếm, nhưng `link` của
      // thông báo có thể trỏ ra ngoài ứng dụng (tải trang thật).
      void call<void>(`${BASE}/${n.id}/da-doc`, { method: 'POST', body: '{}', keepalive: true })
        .then(refresh, () => undefined)
    }
    onNavigate(n.link)
  }

  /** Ba nguồn, ba câu — chép đúng ba nhánh của bản cũ (`Layout.tsx:567`). */
  const headline = (n: NotifRow): string =>
    n.kind === 'broadcast' ? t('nav.notif.kindBroadcast', { name: n.byName })
      : n.kind === 'assign' ? t('nav.notif.kindAssign', { name: n.byName })
        : t('nav.notif.kindMention', { name: n.byName })

  return (
    <div className="relative">
      <button onClick={() => { if (!open) refresh(); setOpen(!open) }}
              aria-label={t('nav.notif.title')}
              title={unread > 0 ? t('nav.notif.unreadTitle', { n: unread }) : t('nav.notif.title')}
              className="relative text-sm px-2 py-1 rounded-full bg-white/20 hover:bg-white/30">
        🔔
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold leading-4 text-center">
            {badge(unread)}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => { setOpen(false) }} />
          <div className="absolute right-0 top-full mt-1.5 z-50 bg-white border border-slate-200 rounded-xl shadow-xl w-80 text-slate-700 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                {t('nav.notif.header', { name: meName })}
              </span>
              {unread > 0 && (
                <button className="text-[10px] font-semibold text-sky-600 hover:underline"
                        onClick={() => {
                          void call<{ marked: number }>(`${BASE}/da-doc-het`, { method: 'POST', body: '{}' })
                            .then(refresh, () => undefined)
                        }}>
                  {t('nav.notif.markAll')}
                </button>
              )}
            </div>

            {rows.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-slate-400 italic">
                {t('nav.notif.empty')}<br />{t('nav.notif.emptyHint')}
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto">
                {rows.map((n) => (
                  <div key={n.id}
                       className={`flex gap-2 px-3 py-2 border-b border-slate-50 hover:bg-sky-50/60 ${n.readAt === null ? 'bg-sky-50/40' : ''}`}>
                    <button onClick={() => { go(n) }} className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-1">
                        {n.readAt === null && <span className="w-1.5 h-1.5 rounded-full bg-sky-500 shrink-0" />}
                        <span className="text-[11px] font-bold text-slate-700 truncate">{headline(n)}</span>
                      </div>
                      {/* L19 (rà 16/10): THỨ TỰ DÒNG như bản cũ `Layout.tsx:570-571` — dòng 2 là LỜI NHẮN (`msg`), dòng 3
                          chữ nhỏ là TÊN VIỆC (`itemTitle`). Trước đây đảo ngược: tên việc lên trên, lời nhắn xuống dưới. */}
                      {n.message !== '' && (
                        <div className="text-[11px] text-slate-500 truncate">{n.message}</div>
                      )}
                      <div className="text-[10px] text-slate-400 truncate">{n.title}</div>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Nói THẲNG trạng thái email — bản cũ ghi rõ vì sao: người dùng từng tưởng app có gửi
                thư mà mãi không thấy. */}
            <div className="px-3 py-1.5 border-t border-slate-100 bg-amber-50/60 text-[9px] text-amber-700 leading-snug">
              {t('nav.notif.mailOff')}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
